import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { CheckCircle, XCircle, Gavel, AlertTriangle, Sparkles, Image as ImageIcon, ClipboardCheck } from "lucide-react";
import moment from "moment";
import { getScoreByPackage, resolveAppeal } from "../../services/deliveryQualityService";
import { showToast } from "../../utils/showToast";
import { apiErrorStatus, apiErrorMessage, apiErrorData } from "../../utils/apiErrorMessage";
import Spinner from "../spinner/spinner";

const RESOLVED_STATUSES = ["approved", "rejected"];

// `detail` exactos de delivery-quality (app/api/scores.py). Un 403/404 sin
// ellos viene de otra capa (p. ej. el AdminOnlyMiddleware del BFF responde
// 403 {message:'Forbidden'} a roles sin permiso) y no significa lo mismo.
const DETAIL_OTHER_COMPANY = "Reviewer is not from the package's company";
const DETAIL_SCORE_NOT_FOUND = "Score not found for this package";

// Aspectos evaluados por la IA dentro de score.scores.imagesAi.
// Cada uno es { score, estado, detalle } con estado "VALIDO" | "INVALIDO".
const ASPECT_KEYS = ["packageLabelAi", "addressNumberAi", "receiverEvidenceAi"];
const ASPECT_LABELS = {
  packageLabelAi: "Etiqueta del paquete",
  addressNumberAi: "Numeración del domicilio",
  receiverEvidenceAi: "Evidencia del receptor",
};

export default function AppealTab({ incident, onUpdated, readOnly }) {
  const { t } = useTranslation();
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  // Resultado de la última resolución ({ tone: "success" | "error", message }),
  // también en línea: dentro del Drawer de /admin/incidents el toast queda
  // tapado por el overlay (ambos z 9999). Los padres recargan la incidencia
  // sin desmontar este tab, así que el aviso sobrevive al refresco.
  const [resolveNotice, setResolveNotice] = useState(null);
  const [score, setScore] = useState(null);
  const [loadingScore, setLoadingScore] = useState(true);
  const [aspects, setAspects] = useState({
    packageLabelAi: "INVALIDO",
    addressNumberAi: "INVALIDO",
    receiverEvidenceAi: "INVALIDO",
  });

  const appeal = incident.appeal || {};
  const packageId = appeal.packageId || incident.packageId;
  const isResolved = RESOLVED_STATUSES.includes(appeal.status);

  useEffect(() => {
    let alive = true;
    if (!packageId) {
      setLoadingScore(false);
      return undefined;
    }
    setLoadingScore(true);
    getScoreByPackage(packageId)
      .then((r) => {
        if (!alive) return;
        const data = r.data;
        setScore(data);
        // Inicializa cada toggle desde el estado de la IA (default "INVALIDO").
        const imagesAi = data?.scores?.imagesAi || {};
        setAspects({
          packageLabelAi: imagesAi.packageLabelAi?.estado || "INVALIDO",
          addressNumberAi: imagesAi.addressNumberAi?.estado || "INVALIDO",
          receiverEvidenceAi: imagesAi.receiverEvidenceAi?.estado || "INVALIDO",
        });
      })
      .catch(() => {
        if (alive) setScore(null);
      })
      .finally(() => {
        if (alive) setLoadingScore(false);
      });
    return () => {
      alive = false;
    };
  }, [packageId]);

  const imagesAi = score?.scores?.imagesAi || {};
  const verdict = imagesAi.verdict;
  const confidence = imagesAi.confidence;
  const images = imagesAi.imagesAnalyzed || [];

  const setAspect = (key, value) =>
    setAspects((prev) => ({ ...prev, [key]: value }));

  const handleResolve = async (resolution) => {
    if (submitting) return;
    setSubmitting(true);
    setResolveNotice(null);
    const fail = (message) => {
      setResolveNotice({ tone: "error", message });
      showToast(message, false);
    };
    try {
      const payload = {
        packageId,
        resolution,
        note: note.trim() || undefined,
      };
      if (resolution === "approved") {
        payload.aspects = {
          packageLabelAi: aspects.packageLabelAi,
          addressNumberAi: aspects.addressNumberAi,
          receiverEvidenceAi: aspects.receiverEvidenceAi,
        };
      }
      const data = await resolveAppeal(payload);
      if (data && data.ok) {
        const message =
          resolution === "approved"
            ? t("Apelación aprobada")
            : t("Apelación rechazada");
        setResolveNotice({ tone: "success", message });
        showToast(message, true);
        setNote("");
        onUpdated && onUpdated();
      } else {
        fail(t("Error al resolver la apelación"));
      }
    } catch (err) {
      // El backend (FastAPI) devuelve el `detail` en inglés y técnico, así que
      // se mapea por status a un texto propio en español. El 409 significa que
      // otro revisor ya la resolvió (concurrencia o modal con datos viejos):
      // se refresca para que desaparezcan los botones en vez de dejar al
      // usuario reintentando en vano.
      // El 403 y el 404 solo se traducen cuando traen el `detail` exacto de
      // FastAPI; si no, se usa el texto del backend o un genérico.
      const status = apiErrorStatus(err);
      const detail = apiErrorData(err)?.detail;
      if (status === 409) {
        fail(t("Esta apelación ya fue resuelta."));
        onUpdated && onUpdated();
      } else if (status === 403 && detail === DETAIL_OTHER_COMPANY) {
        fail(t("No puedes resolver apelaciones de otra empresa."));
      } else if (status === 403) {
        fail(apiErrorMessage(err, t("No tienes permisos para resolver apelaciones.")));
      } else if (status === 404 && detail === DETAIL_SCORE_NOT_FOUND) {
        fail(t("El paquete no tiene evaluación."));
      } else {
        fail(apiErrorMessage(err, t("Error al resolver la apelación")));
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      {/* Banner de estado */}
      {isResolved ? (
        <div className="tab-section__resolved-banner">
          <CheckCircle size={16} />
          {appeal.status === "approved"
            ? t("Apelación aprobada")
            : t("Apelación rechazada")}
          {appeal.createdAt && (
            <span style={{ marginLeft: "auto", fontSize: "0.78rem", fontWeight: 400 }}>
              {moment(appeal.createdAt).format("DD/MM/YYYY HH:mm")}
            </span>
          )}
        </div>
      ) : (
        <div className="tab-section__pending-banner">
          <AlertTriangle size={16} />
          {t("Apelación pendiente de revisión")}
        </div>
      )}

      {/* Información de la apelación */}
      <div className="tab-section">
        <div className="tab-section__title">
          <Gavel size={14} />
          {t("Detalle de la apelación")}
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.6rem" }}>
          <div className="incident-modal__info-card">
            <div className="incident-modal__info-content">
              <span className="incident-modal__info-label">{t("Aspecto apelado")}</span>
              <span className="incident-modal__info-value" style={{ fontSize: "0.8rem" }}>
                {appeal.aspect || "—"}
              </span>
            </div>
          </div>
          <div className="incident-modal__info-card">
            <div className="incident-modal__info-content">
              <span className="incident-modal__info-label">{t("Estado")}</span>
              <span className="incident-modal__info-value" style={{ fontSize: "0.8rem" }}>
                {appeal.status === "approved"
                  ? t("Aprobada")
                  : appeal.status === "rejected"
                    ? t("Rechazada")
                    : t("Pendiente")}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Motivo de la apelación */}
      <div className="tab-section">
        <div className="tab-section__title">
          <Gavel size={14} />
          {t("Motivo de la apelación")}
        </div>
        <div className="tab-section__description-box">
          {appeal.reason || "—"}
        </div>
      </div>

      {/* Imágenes evaluadas (foco de la revisión manual) */}
      {loadingScore ? (
        <div className="tab-section">
          <div style={{ display: "flex", justifyContent: "center", padding: "0.75rem" }}>
            <Spinner />
          </div>
        </div>
      ) : images.length > 0 ? (
        <div className="tab-section">
          <div className="tab-section__title">
            <ImageIcon size={14} />
            {t("Evidencia de la entrega")} ({images.length})
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "0.6rem" }}>
            {images.map((src, i) => (
              <a
                key={i}
                href={src}
                target="_blank"
                rel="noopener noreferrer"
                title={src}
              >
                <img
                  className="dark-safe-img"
                  src={src}
                  alt={`evidencia-${i}`}
                  style={{
                    width: 132,
                    height: 132,
                    objectFit: "cover",
                    borderRadius: 6,
                    border: "1px solid var(--border-primary)",
                  }}
                />
              </a>
            ))}
          </div>
        </div>
      ) : null}

      {/* Veredicto de la IA (contexto) */}
      <div className="tab-section">
        <div className="tab-section__title">
          <Sparkles size={14} />
          {t("Evaluación de la IA")}
        </div>
        {loadingScore ? (
          <div style={{ display: "flex", justifyContent: "center", padding: "0.75rem" }}>
            <Spinner />
          </div>
        ) : !score ? (
          <div className="tab-section__description-box">
            {t("No hay evaluación de calidad para este paquete.")}
          </div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.6rem" }}>
            <div className="incident-modal__info-card">
              <div className="incident-modal__info-content">
                <span className="incident-modal__info-label">{t("Veredicto IA")}</span>
                <span className="incident-modal__info-value" style={{ fontSize: "0.8rem" }}>
                  {verdict || "—"}
                </span>
              </div>
            </div>
            <div className="incident-modal__info-card">
              <div className="incident-modal__info-content">
                <span className="incident-modal__info-label">{t("Confianza")}</span>
                <span className="incident-modal__info-value" style={{ fontSize: "0.8rem" }}>
                  {confidence || "—"}
                </span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Evaluación manual por aspecto */}
      {!loadingScore && score && (
        <div className="tab-section">
          <div className="tab-section__title">
            <ClipboardCheck size={14} />
            {t("Evaluación manual de la evidencia")}
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: "0.6rem" }}>
            {ASPECT_KEYS.map((key) => {
              const aiAspect = imagesAi[key] || {};
              const value = aspects[key];
              return (
                <div
                  key={key}
                  className="incident-modal__info-card"
                  style={{ display: "flex", flexDirection: "column", gap: "0.4rem" }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "0.5rem", flexWrap: "wrap" }}>
                    <div style={{ minWidth: 0 }}>
                      <span className="incident-modal__info-label">{t(ASPECT_LABELS[key])}</span>
                      {aiAspect.detalle && (
                        <div style={{ fontSize: "0.72rem", color: "var(--text-tertiary)", marginTop: "0.15rem" }}>
                          {aiAspect.detalle}
                        </div>
                      )}
                    </div>
                    {!isResolved && !readOnly && (
                      <div style={{ display: "inline-flex", borderRadius: 6, overflow: "hidden", border: "1px solid var(--border-primary)", flexShrink: 0 }}>
                        <button
                          type="button"
                          onClick={() => setAspect(key, "VALIDO")}
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "0.25rem",
                            padding: "0.3rem 0.6rem",
                            fontSize: "0.74rem",
                            border: "none",
                            cursor: "pointer",
                            background: value === "VALIDO" ? "var(--status-success, #198754)" : "transparent",
                            color: value === "VALIDO" ? "#fff" : "var(--text-secondary)",
                          }}
                        >
                          <CheckCircle size={13} />
                          {t("Válido")}
                        </button>
                        <button
                          type="button"
                          onClick={() => setAspect(key, "INVALIDO")}
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "0.25rem",
                            padding: "0.3rem 0.6rem",
                            fontSize: "0.74rem",
                            border: "none",
                            cursor: "pointer",
                            background: value === "INVALIDO" ? "var(--status-error, #dc3545)" : "transparent",
                            color: value === "INVALIDO" ? "#fff" : "var(--text-secondary)",
                          }}
                        >
                          <XCircle size={13} />
                          {t("No válido")}
                        </button>
                      </div>
                    )}
                    {(isResolved || readOnly) && (
                      <span
                        className="incident-modal__info-value"
                        style={{ fontSize: "0.78rem", flexShrink: 0 }}
                      >
                        {value === "VALIDO" ? t("Válido") : t("No válido")}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Resultado de la última resolución. Va fuera del bloque de acciones:
          tras resolver (o tras un 409) el refresco esconde los botones y el
          aviso debe seguir visible. */}
      {resolveNotice && (
        <div
          role={resolveNotice.tone === "error" ? "alert" : "status"}
          className="tab-section__description-box"
          style={{
            display: "flex",
            alignItems: "flex-start",
            gap: "0.5rem",
            marginBottom: "0.75rem",
            color:
              resolveNotice.tone === "error"
                ? "rgb(var(--status-error))"
                : "rgb(var(--status-success))",
          }}
        >
          {resolveNotice.tone === "error" ? (
            <AlertTriangle size={16} style={{ flexShrink: 0, marginTop: "0.1rem" }} aria-hidden />
          ) : (
            <CheckCircle size={16} style={{ flexShrink: 0, marginTop: "0.1rem" }} aria-hidden />
          )}
          <span>{resolveNotice.message}</span>
        </div>
      )}

      {/* Acciones de revisión (solo si no resuelto y no readOnly) */}
      {!isResolved && !readOnly && (
        <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
          <textarea
            className="comment-textarea"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder={t("Nota de revisión (opcional)...")}
            rows={2}
          />
          <div style={{ display: "flex", gap: "0.5rem" }}>
            <button
              type="button"
              className="btn-resolve btn-resolve--primary"
              onClick={() => handleResolve("approved")}
              disabled={submitting}
              style={{ flex: 1, justifyContent: "center" }}
            >
              {submitting ? <Spinner /> : <CheckCircle size={16} />}
              {t("Aprobar apelación (guardar evaluación)")}
            </button>
            <button
              type="button"
              className="btn-resolve btn-resolve--outline"
              onClick={() => handleResolve("rejected")}
              disabled={submitting}
              style={{ flex: 1, justifyContent: "center" }}
            >
              {submitting ? <Spinner /> : <XCircle size={16} />}
              {t("Rechazar apelación")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
